<template>
  <b-card class="mb-50 p-0 address-details-card border-0">
    <div class="row">
      <div class="col-9">
        <div class="row">
          <div class="col-12 label-text color-neutral-black">
            Shipping address
          </div>
        </div>
        <!-- Shipping Address -->
        <div
          v-if="selectedShippingAddress"
          class="row mt-50 address-value"
        >
          <div class="col-6">
            <div>{{ selectedShippingAddress.contactName }},</div>
            <div>{{ selectedShippingAddress.addressLine1 }}, {{ selectedShippingAddress.addressLine2 }},</div>
            <div>
              {{ selectedShippingAddress.city }}, {{ selectedShippingAddress.country }} - <span>{{ selectedShippingAddress.zipcode }}</span>
            </div>
          </div>
          <div class="col-6">
            <div>Logistic Contact: {{ selectedShippingAddress.contactNumber }}</div>
            <div class="text-lowercase">
              {{ selectedShippingAddress.contactEmail }}
            </div>
          </div>
        </div>
        <div v-else>
          -
        </div>
        <!-- Notes and Expected Delivery Window -->
        <div class="row mt-1">
          <div class="col-6">
            <div>
              <span class="label-text color-neutral-black">
                Notes
              </span>
              <b-button
                v-if="isEditNotes"
                class="ml-25"
                size="sm"
                :disabled="isSavingNotes"
                variant="custom-primary"
                @click="onUpdateNotes"
              >
                {{ isSavingNotes ? 'Saving' : 'Save' }}
              </b-button>
              <span
                v-if="!isEditNotes"
                class="color-actions-text-grey ml-10"
              >
                <feather-icon
                  icon="EditIcon"
                  size="15"
                  class="edit-icon cursor-pointer"
                  @click="handleEditNote"
                />
              </span>
            </div>
            <div class="mt-50">
              <b-form-textarea
                v-if="isEditNotes"
                v-model="cart.notes"
                placeholder="Enter Notes"
                @input="(value) => debouncedTrackNoteInput(value, cart)"
              />
              <span
                v-else
                class="color-actions-text-grey"
              >{{ cart.notes || '-' }}</span>
            </div>
          </div>
          <!-- Requested Delivery Window -->
          <div class="col-6">
            <div class="field-heading">
              Requested Delivery Window:
              <span
                v-if="!isEditDeliveryWindow"
                class="field-value"
              >
                {{ expectedDeliveryDate || '-' }}
                <feather-icon
                  icon="EditIcon"
                  size="15"
                  class="edit-icon cursor-pointer"
                  @click="handleEditDeliveryWindow"
                />
              </span>
              <span
                v-if="isEditDeliveryWindow"
                class="float-right exp-av-date"
              >
                <flat-pickr
                  ref="deliveryWindowDate"
                  v-model="expectedDeliveryDate"
                  class="form-control exp-calender"
                  :config="deliveryDateConfig"
                  :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
                />
                <feather-icon
                  icon="CalendarIcon"
                  size="20"
                  class="float-right calender-icon"
                  @click="toggleDeliveryWindowPicker"
                />
                <b-button
                  class="ml-50 mt-50"
                  size="sm"
                  :disabled="isSavingDeliveryWindow"
                  variant="custom-primary"
                  @click="onUpdateDeliveryWindow"
                >
                  {{ isSavingDeliveryWindow ? 'Saving' : 'Save' }}
                </b-button>
              </span>
            </div>
          </div>
        </div>

      </div>
      <!-- Select address button -->
      <div class="col-3 d-flex justify-content-end">
        <div v-b-tooltip.hover="!selectedShippingAddress ? 'No address found' : ''">
          <b-button
            variant="outline-primary"
            :disabled="!selectedShippingAddress"
            @click="onClickSelectAddress"
          >
            Select Another Address
          </b-button>
        </div>
        <b-modal
          :id="'select_address_modal' + cart._id"
          size="lg"
          centered
          title="Select Address"
          hide-footer
        >
          <address-list
            class="mb-1"
            :address-type="ADDRESSES_TYPE.SHIPPING"
            :is-popup="true"
            :is-multiple-cart="true"
            :address="shippingAddress"
            :commons="countryData"
            :set-as-primary-address="setAsPrimaryAddress"
          />
        </b-modal>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
 BButton, BCard, BFormTextarea, BModal, VBTooltip,
} from 'bootstrap-vue'
import { UPDATE_CART_NOTES, UPDATE_CART_EXPECTED_DELIVERY_DATE } from '@/store/modules/shop.module'
import { parseDeliveryDate } from '@/composables/useCartTotal'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { mapState } from 'vuex'
import analytics from '@/@core/utils/analytics'
import constants, { VUE_FLATPICKR_MONTH_CONFIG, MONTH_START_DATE } from '@/constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { debounce } from 'lodash'
import flatPickr from 'vue-flatpickr-component'
import AddressList from '../account-setting/multiple-address/AddressList.vue'
import AddressMixin from '../account-setting/multiple-address/AddressMixin.vue'

const { ADDRESSES_TYPE, KP_MONTH_FORMAT } = c

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'AddressSection',
  components: {
    BCard,
    BButton,
    BFormTextarea,
    BModal,
    AddressList,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [AddressMixin],
  props: {
    cart: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isEditNotes: false,
      isSavingNotes: false,
      isEditDeliveryWindow: false,
      isSavingDeliveryWindow: false,
      expectedDeliveryDate: '',
      selectedShippingAddress: null,
      ADDRESSES_TYPE,
      addresses: [],
      countryData: null,
      KP_MONTH_FORMAT,
    }
  },
  computed: {
    ...mapState({
      commons: state => state.common.commonData,
    }),
    deliveryDateConfig() {
      return VUE_FLATPICKR_MONTH_CONFIG
    },
  },
  created() {
    this.loadCountries()
    this.userId = this.cart?.userId
    this.selectAddressModalId = `select_address_modal${this.cart._id}`
    this.addresses = this.cart?.addresses || []
    if (this.addresses) {
      this.loadShippingAddress()
    }
    // Initialize expected delivery date from cart
    if (this.cart?.expectedDeliveryDate) {
      this.expectedDeliveryDate = parseDeliveryDate(this.cart.expectedDeliveryDate)
    }
  },
  methods: {
    loadCountries() {
      this.$store.dispatch(GET_COUNTRIES).then(res => {
        this.countryData = res?.data?.data
      }).catch(err => {
        apiToastError(err)
      })
    },
    onClickSelectAddress() {
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_SELECT_ANOTHER_ADDRESS_BUTTON, { shippingAddress: this.shippingAddress })
      this.$bvModal.show(this.selectAddressModalId)
    },
    handleEditNote() {
      this.isEditNotes = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_NOTES_ICON, {
        cart: this.cart,
      })
    },
    debouncedTrackNoteInput: debounce((textInput, cart) => {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_ENTERS_TEXT_IN_NOTES, {
        textInput,
        cart,
      })
    }, 500),
    async onUpdateNotes() {
      this.isSavingNotes = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_SAVE_NOTES_BTN, {
        cart: this.cart,
        textInput: this.cart.notes,
      })
      this.$store.dispatch(UPDATE_CART_NOTES, { payload: { cartId: this.cart._id, notes: this.cart.notes } }).then(res => {
        this.isSavingNotes = false
        this.isEditNotes = false
        apiToastSuccess(res.data.message)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_NOTE_SUCCESS, {
          cart: this.cart,
          response: res,
        })
      }).catch(err => {
        this.isSavingNotes = false
        apiToastError(err)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_NOTE_FAILED, {
          cart: this.cart,
          error: err,
        })
     })
    },
    loadShippingAddress() {
      this.selectedShippingAddress = this.addresses.find(address => address?.addressType === ADDRESSES_TYPE.SHIPPING && address?.isPrimary)
      this.getBillingAndShippingAddress(this.addresses)
    },
    handleEditDeliveryWindow() {
      this.isEditDeliveryWindow = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_DELIVERY_WINDOW_ICON, {
        cart: this.cart,
      })
      this.$nextTick(() => {
        this.toggleDeliveryWindowPicker()
      })
    },
    toggleDeliveryWindowPicker() {
      if (this.$refs.deliveryWindowDate?.$el?._flatpickr) {
        this.$refs.deliveryWindowDate.$el._flatpickr.toggle()
      }
    },
    isValidDeliveryDate() {
      if (!this.expectedDeliveryDate) return false
      const [month, year] = this.expectedDeliveryDate.split('/')
      const selectedDate = new Date(year, month - 1, 1)
      const now = new Date()
      const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      return selectedDate >= currentMonth
    },
    async onUpdateDeliveryWindow() {
      if (!this.isValidDeliveryDate()) {
        apiToastError('Please select current or future month')
        return
      }
      this.isSavingDeliveryWindow = true
      const formattedDate = `${MONTH_START_DATE}${this.expectedDeliveryDate}`
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_SAVE_DELIVERY_WINDOW_BTN, {
        cart: this.cart,
        expectedDeliveryDate: formattedDate,
      })
      this.$store.dispatch(UPDATE_CART_EXPECTED_DELIVERY_DATE, {
        payload: {
          cartId: this.cart._id,
          expectedDeliveryDate: formattedDate,
        },
      }).then(res => {
        this.isSavingDeliveryWindow = false
        this.isEditDeliveryWindow = false
        this.cart.expectedDeliveryDate = formattedDate
        apiToastSuccess(res.data.message)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_DELIVERY_WINDOW_SUCCESS, {
          cart: this.cart,
          response: res,
        })
      }).catch(err => {
        this.isSavingDeliveryWindow = false
        apiToastError(err)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_DELIVERY_WINDOW_FAILED, {
          cart: this.cart,
          error: err,
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "src/@core/scss/base/bootstrap-extended/_variables.scss";

.address-details-card {
  background-color: $gray-200;

  .label-text {
    font-size: 1rem;
    font-weight: 700;
  }

  .address-value {
    color: $actions-text-grey
  }

  .field-heading {
    color: $neutral-black;
    font-weight: 600;
  }

  .field-value {
    font-weight: 500;
    color: $actions-text-grey;
  }
}
</style>
